/**
 * Shared CHI Alliance events data — single source of truth.
 * Used by CalendarOfEvents (list/calendar view) and EventDetail (landing page).
 */
import type { CHIEventCategory } from "@shared/eventCategories";

export type { CHIEventCategory } from "@shared/eventCategories";

export interface CHIEvent {
  id: number;
  slug: string;
  date: string;
  displayDate: string;
  time: string;
  title: string;
  subtitle: string;
  category: CHIEventCategory;
  type: "virtual" | "in-person";
  location: string;
  speaker: string;
  speakerOrg: string;
  speakerBio?: string;
  color: string;
  seats: number;
  registered: number;
  /** Server-calculated remaining inventory for managed ticket events, when available. */
  seatsRemaining?: number;
  description: string;
  highlights: string[];
  agenda?: { time: string; item: string }[];
  heroImage?: string; // public media URL
  /** Event artwork for featured/event surfaces, kept separate from a speaker headshot. */
  eventImage?: string;
  partnerLogo?: string; // verified partner logo asset composited into generated hero cutouts
  portraitImage?: string; // authentic speaker portrait for speaker-forward layouts
  /** YouTube/Vimeo embed or application-managed video selected by an administrator. */
  videoUrl?: string;
  timezone?: "CT" | "ET" | "MT" | "PT"; // defaults to CT
  joinUrl?: string; // secure external meeting or registration URL
  /** External registration destination opened only after the local Calendar registration succeeds. */
  registrationRedirectUrl?: string;
  /** Explicit organizer-maintained registration classification for Calendar discovery. */
  registrationMode?: "free" | "paid";
  /** Approval-queue (special) event. Ordinary events omit this or set it false. */
  isSpecialEvent?: boolean;
}

export const EVENTS: CHIEvent[] = [
  {
    id: 1,
    slug: "hr-leaders-financial-decision-makers",
    date: "2026-08-12",
    displayDate: "Aug 12, 2026",
    time: "7:00 PM – 10:00 PM CST",
    title: "HR Leaders as Financial Decision Makers",
    subtitle: "In Partnership with SafetyWing",
    partnerLogo: "/media/events/safetywing-logo.png",
    category: "HR",
    type: "in-person",
    location: "Dallas, TX (Venue TBD)",
    speaker: "Panel Discussion",
    speakerOrg: "SafetyWing x CHI Alliance",
    speakerBio:
      "A curated panel of HR executives and financial leaders exploring the evolving role of HR in organizational financial strategy.",
    color: "#F5A623",
    heroImage: "/media/events/professional-panel.webp",
    eventImage: "/media/events/hr-financial-decision-makers-event.webp",
    seats: 40,
    registered: 28,
    description:
      "An exclusive dinner conversation on how teams are thinking about HR leaders as financial decision-makers. What's working, what's changing, and what peers are actually doing. No pitches — just good food, disruptive mindset, and honest perspectives you don't usually get in webinars or panels.",
    highlights: [
      "Exclusive in-person dinner format",
      "Peer-to-peer discussion",
      "Collaboration & fun",
      "Limited to 40 attendees",
    ],
    agenda: [
      { time: "6:00 PM", item: "Doors open & welcome reception" },
      { time: "6:30 PM", item: "Dinner service begins" },
      { time: "7:00 PM", item: "Panel discussion: HR as financial decision-makers" },
      { time: "8:00 PM", item: "Open Q&A and peer discussion" },
      { time: "8:30 PM", item: "Networking & closing" },
    ],
    timezone: 'CT',
  },
  {
    id: 2,
    slug: "stop-answering-same-questions-ai-hr-assistant",
    date: "2026-08-26",
    displayDate: "Aug 26, 2026",
    time: "1:00 PM CST",
    title: "Stop Answering the Same Questions",
    subtitle: "Build an AI HR Assistant That Works for You",
    category: "AI / Tech",
    type: "virtual",
    location: "Zoom (link sent after registration)",
    speaker: "Nahed Khairallah",
    speakerOrg: "Organized Chaos",
    speakerBio:
      "Nahed Khairallah is an HR technology strategist and founder of Organized Chaos, helping HR teams leverage AI to eliminate repetitive work and focus on what matters most.",
    color: "#00D4FF",
    eventImage: "/media/events/ai-hr-assistant-event.webp",
    seats: 200,
    registered: 87,
    description:
      "AI is everywhere, but how do you actually use it to solve real HR challenges? Join Nahed Khairallah for a live, hands-on virtual roundtable where he'll show you exactly how to build an 'Ask HR' AI assistant using your own employee handbook and HR documentation.",
    highlights: [
      "Build an AI-powered 'Ask HR' assistant live",
      "Turn your handbook into an intelligent knowledge base",
      "Reduce repetitive HR questions and admin work",
      "No technical expertise required",
    ],
    agenda: [
      { time: "1:00 PM", item: "Welcome and introductions" },
      { time: "1:10 PM", item: "The problem: why HR teams waste hours on repetitive questions" },
      { time: "1:25 PM", item: "Live demo: building an AI HR assistant step-by-step" },
      { time: "2:00 PM", item: "Q&A and open discussion" },
      { time: "2:30 PM", item: "Wrap-up and next steps" },
    ],
    timezone: 'CT',
  },
  {
    id: 3,
    slug: "scaling-hiring-infrastructure-growth",
    date: "2026-09-09",
    displayDate: "Sep 9, 2026",
    time: "1:00 PM CST",
    title: "Scaling Your Hiring Infrastructure for Growth",
    subtitle: "",
    category: "Talent",
    type: "virtual",
    location: "Zoom (link sent after registration)",
    speaker: "Michael Goldberg",
    speakerOrg: "Hiring Transformed",
    speakerBio:
      "Michael Goldberg is the founder of Hiring Transformed, a consulting firm that helps growing organizations build scalable, repeatable hiring systems that support rapid expansion.",
    color: "#E91E8C",
    heroImage: "/media/speakers/michael-goldberg-headshot.jpg",
    eventImage: "/media/events/scaling-hiring-infrastructure-event.webp",
    seats: 200,
    registered: 62,
    description:
      "The hiring infrastructure that supported your first 20 employees will not be enough when your organization begins hiring 50 or more people. Michael will explore the systems, processes, and leadership practices required to support hiring at greater scale.",
    highlights: [
      "Evaluate your current hiring infrastructure",
      "Identify systems and roles needed to scale",
      "Create consistency and accountability in hiring",
      "Implement a structured hiring process immediately",
    ],
    agenda: [
      { time: "1:00 PM", item: "Welcome and framing the challenge" },
      { time: "1:10 PM", item: "Diagnosing your current hiring infrastructure" },
      { time: "1:30 PM", item: "The scaling framework: systems, roles, and accountability" },
      { time: "2:00 PM", item: "Live Q&A" },
      { time: "2:30 PM", item: "Action planning and wrap-up" },
    ],
    timezone: 'CT',
  },
  {
    id: 4,
    slug: "disrupthr-dallas-6",
    date: "2026-09-17",
    displayDate: "Sep 17, 2026",
    time: "4:30 PM – 7:30 PM CST",
    title: "DisruptHR Dallas 6.0",
    subtitle: "Bold Ideas. Fresh Perspectives. Meaningful Connections.",
    category: "HR",
    type: "in-person",
    location: "The Board Studios, Dallas, TX",
    speaker: "Multiple Speakers",
    speakerOrg: "DisruptHR Dallas",
    speakerBio:
      "DisruptHR Dallas brings together HR innovators, executives, and thought leaders for fast-paced, high-impact talks that challenge conventional thinking about work, leadership, and culture.",
    color: "#A855F7",
    eventImage: "/media/events/disrupthr-dallas-event.webp",
    seats: 150,
    registered: 114,
    description:
      "Join HR leaders, innovators, entrepreneurs, and workplace disruptors at DisruptHR Dallas 6.0 for an evening of bold ideas, fresh perspectives, and meaningful connections. Featuring fast-paced, high-impact talks, this event challenges conventional thinking and explores the future of work, leadership, culture, and innovation.",
    highlights: [
      "Fast-paced, high-impact speaker format",
      "Future of work and leadership focus",
      "Networking with Dallas HR community",
      "The Board Studios — premium venue",
    ],
    registrationRedirectUrl: "https://www.tickettailor.com/events/mindabilityconsultancylimited/2168482",
    agenda: [
      { time: "4:30 PM", item: "Doors open & networking reception" },
      { time: "5:00 PM", item: "Welcome remarks" },
      { time: "5:15 PM", item: "Speaker presentations (5-minute format)" },
      { time: "6:30 PM", item: "Networking break" },
      { time: "7:00 PM", item: "Closing remarks & community announcements" },
      { time: "7:30 PM", item: "Event close" },
    ],
    timezone: 'CT',
  },
  {
    id: 5,
    slug: "hard-conversations-every-leader-needs",
    date: "2026-09-23",
    displayDate: "Sep 23, 2026",
    time: "1:00 PM Central Time",
    title: "The Hard Conversations Every Leader Needs to Master",
    subtitle: "",
    category: "Leadership",
    type: "virtual",
    location: "Zoom (online)",
    speaker: "Farrah Mitra",
    speakerOrg: "Leadership Communication Expert",
    speakerBio:
      "Farrah Mitra is a leadership communication expert who helps executives and managers deliver difficult messages with clarity, empathy, and impact — building trust even in the hardest moments.",
    color: "#F5A623",
    eventImage: "/media/events/hard-conversations-leadership-event.webp",
    seats: 200,
    registered: 45,
    description:
      "Join Farrah Mitra for an interactive session on leading through change with confidence. Learn a practical framework for navigating difficult conversations—whether you're navigating organizational change, performance conversations, or strategic decisions.",
    highlights: [
      "A proven framework for difficult conversations",
      "Communicate with clarity, confidence, and empathy",
      "Build trust and strengthen team relationships",
      "Reduce resistance and increase alignment",
      "Apply practical communication strategies immediately",
    ],
    timezone: 'CT',
    joinUrl: "https://zoom.us/j/95618725513?pwd=nBQClT2v8QUb3FuWptwkqBKQhBwYOs.1",
  },
  {
    id: 6,
    slug: "evidence-based-decision-model",
    date: "2026-11-06",
    displayDate: "Nov 6, 2026",
    time: "1:00 PM CST",
    title: "Make Better Decisions When It Matters Most",
    subtitle: "The Evidence-Based Decision Model™",
    category: "Leadership",
    type: "virtual",
    location: "Zoom (link sent after registration)",
    speaker: "Dr. Diane Dye",
    speakerOrg: "People Risk Consulting",
    speakerBio:
      "Dr. Diane Dye is the founder of People Risk Consulting and creator of the Evidence-Based Decision Model™ — a framework used by executives worldwide to make smarter, more objective decisions under pressure.",
    color: "#E91E8C",
    eventImage: "/media/events/evidence-based-decisions-event.webp",
    seats: 200,
    registered: 38,
    description:
      "Great leaders don't rely on intuition alone — they rely on evidence. Join Dr. Diane Dye for an engaging virtual roundtable where she'll introduce the Evidence-Based Decision Model™, a practical framework designed to help leaders make smarter, more objective decisions while reducing risk and driving greater accountability.",
    highlights: [
      "The Evidence-Based Decision Model™ framework",
      "Make objective, data-informed decisions",
      "Reduce unnecessary risk and decision paralysis",
      "Create accountability and accelerate outcomes",
    ],
    agenda: [
      { time: "1:00 PM", item: "Welcome and introductions" },
      { time: "1:10 PM", item: "The cost of poor decision-making in organizations" },
      { time: "1:25 PM", item: "Introducing the Evidence-Based Decision Model™" },
      { time: "1:50 PM", item: "Case studies and real-world application" },
      { time: "2:10 PM", item: "Q&A and open discussion" },
    ],
    timezone: 'CT',
  },
  {
    id: 9,
    slug: "the-human-advantage-building-community-age-ai",
    date: "2026-10-14",
    displayDate: "Oct 14, 2026",
    time: "1:00 PM – 2:00 PM CST",
    title: "The Human Advantage: Building Community in the Age of AI",
    subtitle: "A virtual roundtable with Tiona Nicholas",
    category: "AI / Tech",
    type: "virtual",
    location: "Virtual roundtable (registration details to follow)",
    speaker: "Tiona Nicholas",
    speakerOrg: "The CHI Alliance",
    color: "#39D7F4",
    heroImage: "/media/speakers/tiona-nicholas.webp",
    eventImage: "/media/events/human-advantage-community-ai-event.webp",
    portraitImage: "/media/speakers/tiona-nicholas.webp",
    seats: 200,
    registered: 0,
    description:
      "Join Tiona for an engaging virtual roundtable discussion on how artificial intelligence is transforming the way we work, lead, and connect. Tiona will explore the qualities that make us human and why they matter more than ever, including what AI cannot replace and how organizations can intentionally strengthen community and human connection as more work becomes automated.",
    highlights: [
      "Explore the human strengths AI cannot replace",
      "Strengthen community and human connection in an automated world",
      "Create more space for empathy, creativity, and collaboration",
      "Build meaningful relationships as work evolves",
    ],
    timezone: 'CT',
  },
  {
    id: 10,
    slug: "hiring-shouldnt-suck-dinner",
    date: "2026-11-05",
    displayDate: "Nov 5, 2026",
    time: "6:30 PM – 9:30 PM CST",
    title: "Hiring Shouldn't Suck Dinner",
    subtitle: "In Partnership with SCALIS",
    partnerLogo: "/media/partners/scalis.webp",
    category: "Talent",
    type: "in-person",
    location: "Dallas, TX (Venue TBD)",
    speaker: "Executive Dinner",
    speakerOrg: "SCALIS x The CHI Alliance",
    color: "#F5A623",
    heroImage: "/media/sponsors/generated/sponsor-scalis-hero.jpg",
    eventImage: "/media/sponsors/generated/sponsor-scalis-hero.jpg",
    seats: 12,
    registered: 0,
    description:
      "Join a select group of Dallas area talent acquisition and HR leaders for an intimate dinner on how AI is reshaping the way we work. We’ll share candid perspectives, practical ideas, and the kind of meaningful connections that are hard to make at a conference.\n\nIf you’re a founder or senior executive leading Talent, People, HR Transformation, or TA Operations, we’d love to have you at the table.",
    highlights: [
      "Exclusive in-person dinner format",
      "Candid conversation about AI and the future of work",
      "Practical ideas from Talent and HR leaders",
      "Meaningful connections with Dallas-area peers",
      "Limited to 12 seats at the table",
    ],
    agenda: [
      { time: "6:30 PM", item: "Arrival and welcome reception" },
      { time: "7:00 PM", item: "Dinner service begins" },
      { time: "7:30 PM", item: "Candid conversation: how AI is reshaping work" },
      { time: "8:30 PM", item: "Peer discussion and meaningful connections" },
      { time: "9:30 PM", item: "Event close" },
    ],
    timezone: "CT",
  },
  {
    id: 8,
    slug: "the-secret-to-everyday-confidence",
    date: "2027-01-20",
    displayDate: "Jan 20, 2027",
    time: "1:00 PM CST",
    title: "The Secret to Everyday Confidence",
    subtitle: "",
    category: "Motivational",
    type: "virtual",
    location: "Zoom (link sent after registration)",
    speaker: "Meshell R. Baker",
    speakerOrg: "Chief Confidence Igniter",
    speakerBio:
      "Meshell R. Baker is a transformational speaker, executive coach, and the Chief Confidence Igniter, dedicated to helping leaders and teams turn self-doubt into confident action. A 3-time Top 100 Women in Sales honoree, Meshell has inspired organizations worldwide by teaching practical strategies to build resilience, elevate performance, and create lasting behavior change. Through her energetic, authentic style and powerful storytelling, she empowers audiences to lead with courage, embrace uncertainty, and unlock their full potential.",
    color: "#E91E8C",
    heroImage: "/media/speakers/meshell-baker.webp",
    eventImage: "/media/events/everyday-confidence-event.webp",
    portraitImage: "/media/speakers/meshell-baker.webp",
    seats: 200,
    registered: 0,
    description:
      "Discover how confidence isn't something you're born with—it's something you build. In this engaging session, Meshell will share actionable tools to strengthen your confidence every day, overcome self-doubt, and step into greater influence and impact in both your personal and professional life.",
    highlights: [
      "Build confidence as an everyday practice",
      "Use actionable tools to overcome self-doubt",
      "Step into greater influence and impact",
      "Strengthen confidence in your personal and professional life",
    ],
    timezone: 'CT',
  },
];

export function getEventBySlug(slug: string): CHIEvent | undefined {
  return EVENTS.find((e) => e.slug === slug);
}

export function slugToEventUrl(slug: string, baseUrl?: string): string {
  const base = baseUrl ?? (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/events/${slug}`;
}
